import { forwardRef } from 'react'
import type { Background } from '@/api/types/background'
import { resolveBackgroundVisual } from '@/lib/backgrounds/resolve-background-visual'
import { cn } from '@/utils'

const GHOST_WIDTH = 96
const GHOST_HEIGHT = 64

/** The ghost's `transform` for a cursor at (clientX, clientY), centering it on the cursor. The
 *  provider writes this straight onto the element on every pointer move (see
 *  `BackgroundPlacementProvider`'s `updateFromClient`), so dragging never re-renders React. */
export function backgroundDragGhostTransform(clientX: number, clientY: number): string {
  return `translate3d(${clientX - GHOST_WIDTH / 2}px, ${clientY - GHOST_HEIGHT / 2}px, 0)`
}

type BackgroundDragGhostProps = {
  background: Background
  /** Cursor position when the drag began. Later moves are applied directly to the DOM via the
   *  forwarded ref, so this must stay constant for the whole drag — React only re-applies a style
   *  prop that changed, which is what keeps it from snapping the ghost back on a re-render. */
  initialClientX: number
  initialClientY: number
  /** True while hovering a valid drop target (a page) — mirrors PhotoDragGhost's green highlight
   *  for a fillable shape. */
  overTarget: boolean
}

/**
 * Small cursor-following preview shown while dragging a background from the panel. Legacy gets
 * this for free from native HTML5 drag-and-drop (the browser's own drag image); this app's
 * pointer-based drag (matching Photos' own `PhotoDragGhost`) needs an explicit one instead.
 *
 * Positioned with `transform` rather than `left`/`top`, so moving it never triggers layout.
 */
export const BackgroundDragGhost = forwardRef<HTMLDivElement, BackgroundDragGhostProps>(
  ({ background, initialClientX, initialClientY, overTarget }, ref) => {
    const visual = resolveBackgroundVisual(background)

    return (
      <div
        ref={ref}
        className={cn(
          'pointer-events-none fixed left-0 top-0 z-[9999] overflow-hidden rounded-lg border-2 shadow-lg transition-[border-color,box-shadow] duration-100 will-change-transform',
          overTarget ? 'border-green-600' : 'border-border'
        )}
        style={{
          transform: backgroundDragGhostTransform(initialClientX, initialClientY),
          width: GHOST_WIDTH,
          height: GHOST_HEIGHT,
          opacity: 0.85,
          boxShadow: overTarget ? '0 0 12px rgba(22,163,74,0.5)' : undefined,
        }}
        data-background-drag-ghost=""
        aria-hidden
      >
        {visual.kind === 'image' ? (
          // Fitted whole, like the placed background (`SpreadBackgroundFills`), so the preview
          // shows the same image framing the drop will produce; the white behind it reads as paper.
          <img
            src={visual.url}
            alt=""
            className="h-full w-full bg-white object-contain object-center"
            decoding="async"
            draggable={false}
          />
        ) : visual.kind === 'color' ? (
          <div className="h-full w-full" style={{ backgroundColor: visual.hex }} />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-muted text-[10px] text-muted-foreground">
            {background.displayName}
          </div>
        )}
      </div>
    )
  }
)

BackgroundDragGhost.displayName = 'BackgroundDragGhost'
